import type { TemplateDefinition } from '../templateTypes'

/**
 * HTML templates for Feed and Story formats ("OnlyJobs").
 *
 * Placeholders use data-placeholder="key" attributes:
 *   data-placeholder="title"           (html)
 *   data-placeholder="label"           (text)
 *   data-placeholder="subtitle"        (html)
 *   data-placeholder="city"            (text)
 *   data-placeholder="employment-type" (text)
 *   data-placeholder="cta-text"        (text)
 *   data-placeholder="logo"            (image — sets src)
 *   data-placeholder="header-image"    (image — sets src)
 *
 * data-placeholder-type can be "text" (default), "html", or "image".
 *
 * CSS custom properties (on :root):
 *   --primary-color   → button bg, icon fill
 *   --accent-color    → secondary accents
 *   --title-color     → title text
 *   --cta-text-color  → button text
 *   --stroke-color    → photo circle stroke, arrow
 *
 * Swoosh decorations are inline SVGs colored via --accent-color.
 * Fonts use Montserrat from /templates/fonts/.
 */

const DEFAULT_FEED_TEMPLATE = `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8">
<style>
/* ── Fonts ── */
@font-face { font-family:'Montserrat'; font-weight:300; src:url('/templates/fonts/montserrat-v31-latin-300.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:400; src:url('/templates/fonts/montserrat-v31-latin-regular.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:500; src:url('/templates/fonts/montserrat-v31-latin-500.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:600; src:url('/templates/fonts/montserrat-v31-latin-600.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:700; src:url('/templates/fonts/montserrat-v31-latin-700.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:800; src:url('/templates/fonts/montserrat-v31-latin-800.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:900; src:url('/templates/fonts/montserrat-v31-latin-900.woff2') format('woff2'); }

:root {
  --primary-color: #C2A676;
  --accent-color: #C2A676;
  --title-color: #4a4a4a;
  --cta-text-color: #FFFFFF;
  --stroke-color: #4a4a4a;
}

* { margin:0; padding:0; box-sizing:border-box; }
body { margin:0; padding:0; }

.main {
  position: relative;
  overflow: hidden;
  width: 1080px;
  height: 1080px;
  background: #ffffff;
  font-family: 'Montserrat', Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1;
  font-weight: 400;
  color: #444;
}

/* ── Logo ── */
.logo {
  display: block;
  z-index: 1;
  position: absolute;
  top: 5.75em;
  right: 5.25em;
  background: transparent;
}
.logo img {
  display: block;
  max-width: 100%;
  height: auto;
  background: transparent;
}
#logo { max-width: 234px; max-height: 156px; object-fit: contain; background: transparent; }
#logo[src=""], #logo:not([src]) { display: none; }

/* ── Swoosh decorations (inline SVG, colored via --accent-color) ── */
.swoosh-top,
.swoosh-bottom {
  z-index: 0;
  position: absolute;
  display: block;
}
.swoosh-top {
  left: 0; top: 0;
  width: 72em; height: 72em;
  pointer-events: none;
}
.swoosh-bottom {
  right: 0; bottom: -0.2em;
  width: 22.5em; height: 58em;
}

/* ── Content ── */
.vacation {
  z-index: 1;
  position: absolute;
  top: 11.5em; left: 6em; right: 23em;
}
#label {
  font-size: 2.7em;
  font-weight: 400;
  padding: 0 0 0.4em 0;
}
#title {
  font-size: 3.8em;
  line-height: 1.1;
  font-weight: 800;
  color: var(--title-color);
}
#subtitle {
  display: block;
  font-size: 2.2em;
  font-weight: 400;
  line-height: 1.2;
  margin: 0.3em 0 0 0;
  color: #6f6f6f;
}
#subtitle:empty { display: none; }

/* ── Specs ── */
.vacation_specs {
  z-index: 3;
  position: absolute;
  top: 36.5em; left: 6em;
  width: 28em;
  display: flex;
  flex-direction: column;
}
.spec-row {
  font-size: 2.5em;
  line-height: 1;
  padding: 0.25em 1em 0.75em 0;
  margin: 0 0 0.3em 0;
  display: flex;
  align-items: center;
  gap: 0.4em;
}
.spec-row svg {
  width: 1.1em; height: 1.1em;
  flex-shrink: 0;
}
.spec-row:has(span:empty) { display: none; }

/* ── Photo circle ── */
.vacation_image {
  z-index: 2;
  position: absolute;
  right: -7em;
  top: 27em;
}
.vacation_image_wrap {
  position: relative;
  display: inline-block;
}
/* Decorative open-circle stroke */
.vacation_image_wrap:before {
  content: "";
  position: absolute;
  background: url('/templates/feed/line_image.png') no-repeat -3% 50.33%;
  top: -2.4em; left: -3em; right: 0; bottom: -2.2em;
  background-size: contain;
  pointer-events: none;
  z-index: 3;
}
.photo-circle {
  width: 37em; height: 37em;
  border-radius: 50%;
  overflow: hidden;
  background: #e8eaed;
}
#header-image {
  display: block;
  width: 100%; height: 100%;
  object-fit: cover;
  background: transparent;
}
#header-image[src=""], #header-image:not([src]) { visibility: hidden; }

/* ── CTA button (CSS, not image) ── */
.btn-wrap {
  z-index: 2;
  position: absolute;
  left: 6em; bottom: 6em;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
/* Arrow decoration */
.btn-arrow {
  z-index: 2;
  position: absolute;
  left: 4em; bottom: 13em;
}
.btn-arrow svg {
  width: 8em; height: 5.2em;
  display: block;
}
.cta-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 22em; height: 6.5em;
  background: var(--primary-color);
  border: none;
  border-radius: 9999px;
  padding: 0 3em;
  box-shadow: -6px 10px 18px rgba(0,0,0,0.22);
}
#cta-text {
  font-size: 2.2em;
  font-weight: 600;
  color: var(--cta-text-color);
  white-space: nowrap;
  font-style: normal;
}
</style>
</head>
<body>
<div class="main">
  <svg class="swoosh-top" viewBox="0 0 1080 1080" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="stg" x1="0" y1="1" x2="0.3" y2="0"><stop offset="0%" stop-color="var(--accent-color)" stop-opacity="0.8"/><stop offset="70%" stop-color="var(--accent-color)" stop-opacity="0.45"/><stop offset="100%" stop-color="var(--accent-color)" stop-opacity="0.15"/></linearGradient><linearGradient id="stg-left" x1="0" y1="1" x2="0.5" y2="0"><stop offset="0%" stop-color="var(--accent-color)" stop-opacity="1"/><stop offset="100%" stop-color="var(--accent-color)" stop-opacity="0.25"/></linearGradient><linearGradient id="stg2" x1="1" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="var(--accent-color)" stop-opacity="0.03"/><stop offset="40%" stop-color="var(--accent-color)" stop-opacity="0.08"/><stop offset="100%" stop-color="var(--accent-color)" stop-opacity="0.25"/></linearGradient></defs><path d="M0,260 Q60,70 240,0 L120,0 Q15,35 0,80 Z" fill="url(#stg-left)"/><path d="M424,0 L589,0 Q699,0 764,22 Q779,55 779,90 Q719,85 424,0 Z" fill="url(#stg2)"/></svg>
  <svg class="swoosh-bottom" viewBox="0 0 338 870" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="sbg" x1="0.5" y1="0" x2="0.5" y2="1"><stop offset="0%" stop-color="var(--accent-color)" stop-opacity="0.02"/><stop offset="50%" stop-color="var(--accent-color)" stop-opacity="0.3"/><stop offset="100%" stop-color="var(--accent-color)" stop-opacity="0.7"/></linearGradient></defs><path d="M338,18 C330,80 30,230 5,460 C-15,650 0,790 30,870 L305,870 C235,810 145,650 130,470 C145,250 330,90 326,18 A6,6 0 0,1 338,18 Z" fill="url(#sbg)"/></svg>

  <a class="logo"><img id="logo" data-placeholder="logo" data-placeholder-type="image" src="/templates/feed/logo.png" alt=""></a>

  <div class="vacation">
    <h2 id="label" data-placeholder="label"></h2>
    <h1 id="title" data-placeholder="title" data-placeholder-type="html"></h1>
    <span id="subtitle" data-placeholder="subtitle" data-placeholder-type="html"></span>
  </div>

  <div class="vacation_specs">
    <div class="spec-row">
      <svg viewBox="0 0 64 80" fill="none"><path d="M32 4C18 4 8 15 8 28c0 18 24 48 24 48s24-30 24-48C56 15 46 4 32 4z" fill="var(--primary-color)"/><circle cx="32" cy="27" r="8" fill="#fff"/></svg>
      <span id="city" data-placeholder="city"></span>
    </div>
    <div class="spec-row">
      <svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="28" fill="var(--primary-color)"/><path d="M32 14V34L44 40" stroke="#fff" stroke-width="5" stroke-linecap="round"/></svg>
      <span id="employment-type" data-placeholder="employment-type"></span>
    </div>
  </div>

  <div class="vacation_image">
    <div class="vacation_image_wrap">
      <div class="photo-circle"><img id="header-image" data-placeholder="header-image" data-placeholder-type="image" src="/templates/feed/head.png" alt=""></div>
    </div>
  </div>

  <div class="btn-arrow">
    <svg viewBox="0 0 110 70" fill="none"><path d="M4 8 C36 2 70 14 76 56" stroke="var(--stroke-color)" stroke-width="5.5" stroke-linecap="round" fill="none"/><path d="M62 50 L76 60 L83 44" stroke="var(--stroke-color)" stroke-width="5.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
  </div>
  <div class="btn-wrap">
    <div class="cta-btn">
      <span id="cta-text" data-placeholder="cta-text"></span>
    </div>
  </div>
</div>
</body>
</html>`

const DEFAULT_STORY_TEMPLATE = `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8">
<style>
/* ── Fonts ── */
@font-face { font-family:'Montserrat'; font-weight:300; src:url('/templates/fonts/montserrat-v31-latin-300.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:400; src:url('/templates/fonts/montserrat-v31-latin-regular.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:500; src:url('/templates/fonts/montserrat-v31-latin-500.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:600; src:url('/templates/fonts/montserrat-v31-latin-600.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:700; src:url('/templates/fonts/montserrat-v31-latin-700.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:800; src:url('/templates/fonts/montserrat-v31-latin-800.woff2') format('woff2'); }
@font-face { font-family:'Montserrat'; font-weight:900; src:url('/templates/fonts/montserrat-v31-latin-900.woff2') format('woff2'); }

:root {
  --primary-color: #C2A676;
  --accent-color: #C2A676;
  --title-color: #4a4a4a;
  --cta-text-color: #FFFFFF;
  --stroke-color: #4a4a4a;
}

* { margin:0; padding:0; box-sizing:border-box; }
body { margin:0; padding:0; }

.main {
  position: relative;
  overflow: hidden;
  width: 1080px;
  height: 1920px;
  background: #ffffff;
  font-family: 'Montserrat', Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1;
  font-weight: 400;
  color: #444;
}

/* ── Logo ── */
.logo {
  display: block;
  z-index: 1;
  position: absolute;
  top: 12em; right: 5em;
  background: transparent;
}
.logo img { display: block; max-width: 100%; height: auto; background: transparent; }
#logo { max-width: 260px; max-height: 182px; object-fit: contain; background: transparent; }
#logo[src=""], #logo:not([src]) { display: none; }

/* ── Swoosh decorations (inline SVG, colored via --accent-color) ── */
.swoosh-top,
.swoosh-bottom {
  z-index: 0;
  position: absolute;
  display: block;
}
.swoosh-top {
  left: 0; top: 0;
  width: 72em; height: 128em;
  pointer-events: none;
}
.swoosh-bottom {
  right: -4em; bottom: 0;
  width: 30em; height: 92.5em;
}

/* ── Content ── */
.vacation {
  z-index: 1;
  position: absolute;
  top: 25em; left: 5em; right: 6em;
}
#label {
  font-size: 3.5em;
  font-weight: 400;
  padding: 0 0 1em 0;
}
#title {
  font-size: 3.8em;
  line-height: 1.1;
  font-weight: 800;
  color: var(--title-color);
}
#subtitle {
  display: block;
  font-size: 2.8em;
  font-weight: 400;
  line-height: 1.2;
  margin: 0.3em 0 0 0;
  color: #6f6f6f;
}
#subtitle:empty { display: none; }

/* ── Specs ── */
.vacation_specs {
  z-index: 3;
  position: absolute;
  top: 58em; left: 5em;
  width: 28em;
  display: flex;
  flex-direction: column;
}
.spec-row {
  font-size: 3.5em;
  line-height: 1.15;
  padding: 0.25em 1em 0.25em 0;
  margin: 0 0 0.2em 0;
  display: flex;
  align-items: center;
  gap: 0.4em;
}
.spec-row svg {
  width: 1em; height: 1em;
  flex-shrink: 0;
}
.spec-row:has(span:empty) { display: none; }

/* ── Photo circle ── */
.vacation_image {
  z-index: 1;
  position: absolute;
  right: -10.5em;
  top: 56.5em;
}
.vacation_image_wrap {
  position: relative;
  display: inline-block;
}
.vacation_image_wrap:before {
  content: "";
  position: absolute;
  background: url('/templates/story/line_image.png') no-repeat -3% 50.33%;
  top: -2.5em; left: -2.9em; right: 0; bottom: -2.5em;
  background-size: contain;
  pointer-events: none;
}
.photo-circle {
  width: 40em; height: 40em;
  border-radius: 50%;
  overflow: hidden;
  background: #e8eaed;
}
#header-image {
  display: block;
  width: 100%; height: 100%;
  object-fit: cover;
  background: transparent;
}
#header-image[src=""], #header-image:not([src]) { visibility: hidden; }

/* ── CTA button (CSS) ── */
.btn-wrap {
  z-index: 2;
  position: absolute;
  left: 5em; bottom: 8em;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.btn-arrow {
  z-index: 2;
  position: absolute;
  left: 4em; bottom: 16.5em;
}
.btn-arrow svg {
  width: 10em; height: 6.2em;
  display: block;
}
.cta-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 26em; height: 8em;
  background: var(--primary-color);
  border: none;
  border-radius: 9999px;
  padding: 0 4em;
  box-shadow: -8px 13px 24px rgba(0,0,0,0.22);
}
#cta-text {
  font-size: 2.8em;
  font-weight: 600;
  color: var(--cta-text-color);
  white-space: nowrap;
  font-style: normal;
}
</style>
</head>
<body>
<div class="main">
  <svg class="swoosh-top" viewBox="0 0 1080 1920" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="stg-story" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stop-color="var(--accent-color)" stop-opacity="0.55"/><stop offset="45%" stop-color="var(--accent-color)" stop-opacity="0.4"/><stop offset="60%" stop-color="var(--accent-color)" stop-opacity="0.15"/><stop offset="100%" stop-color="var(--accent-color)" stop-opacity="0.03"/></linearGradient></defs><path d="M0,280 C100,140 200,80 300,75 C430,65 650,100 870,200 L870,120 C650,30 430,20 300,30 C200,35 100,80 0,190 Z" fill="url(#stg-story)"/></svg>
  <svg class="swoosh-bottom" viewBox="0 0 450 1388" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="sbg" x1="0.5" y1="0" x2="0.5" y2="1"><stop offset="0%" stop-color="var(--accent-color)" stop-opacity="0.02"/><stop offset="50%" stop-color="var(--accent-color)" stop-opacity="0.3"/><stop offset="100%" stop-color="var(--accent-color)" stop-opacity="0.7"/></linearGradient></defs><path d="M450,22 C442,120 40,370 5,730 C-20,1020 0,1240 40,1388 L410,1388 C330,1270 200,1020 180,740 C195,400 440,140 435,22 A8,8 0 0,1 450,22 Z" fill="url(#sbg)"/></svg>

  <a class="logo"><img id="logo" data-placeholder="logo" data-placeholder-type="image" src="/templates/story/logo.png" alt=""></a>

  <div class="vacation">
    <h2 id="label" data-placeholder="label"></h2>
    <h1 id="title" data-placeholder="title" data-placeholder-type="html"></h1>
    <span id="subtitle" data-placeholder="subtitle" data-placeholder-type="html"></span>
  </div>

  <div class="vacation_specs">
    <div class="spec-row">
      <svg viewBox="0 0 64 80" fill="none"><path d="M32 4C18 4 8 15 8 28c0 18 24 48 24 48s24-30 24-48C56 15 46 4 32 4z" fill="var(--primary-color)"/><circle cx="32" cy="27" r="8" fill="#fff"/></svg>
      <span id="city" data-placeholder="city"></span>
    </div>
    <div class="spec-row">
      <svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="28" fill="var(--primary-color)"/><path d="M32 14V34L44 40" stroke="#fff" stroke-width="5" stroke-linecap="round"/></svg>
      <span id="employment-type" data-placeholder="employment-type"></span>
    </div>
  </div>

  <div class="vacation_image">
    <div class="vacation_image_wrap">
      <div class="photo-circle"><img id="header-image" data-placeholder="header-image" data-placeholder-type="image" src="/templates/story/head.png" alt=""></div>
    </div>
  </div>

  <div class="btn-arrow">
    <svg viewBox="0 0 110 70" fill="none"><path d="M4 8 C36 2 70 14 76 56" stroke="var(--stroke-color)" stroke-width="5.5" stroke-linecap="round" fill="none"/><path d="M62 50 L76 60 L83 44" stroke="var(--stroke-color)" stroke-width="5.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
  </div>
  <div class="btn-wrap">
    <div class="cta-btn">
      <span id="cta-text" data-placeholder="cta-text"></span>
    </div>
  </div>
</div>
</body>
</html>`

const onlyjobs: TemplateDefinition = {
  id: 'onlyjobs',
  name: 'OnlyJobs',
  templates: {
    feed: DEFAULT_FEED_TEMPLATE,
    story: DEFAULT_STORY_TEMPLATE,
  },
}

export default onlyjobs
